import React, { LegacyRef, useMemo } from 'react';
import { QuestionProps } from '@de/components/Step/Step';
import { Option } from '@de/types/types';
import { useDynamicExperienceContext } from '@de/providers/DynamicExperienceProvider';
import useQuestionEvents from '@de/hooks/useQuestionEvents';
import { useOfferingsFilterOptions } from '@de/hooks/useOfferingsFilterOptions';
import { ErrorMessage } from '@de/components/ErrorMessage';
import { SearchBar } from '@de/components/SearchBar';

const DynamicMultipleChoiceQuestion: React.FC<QuestionProps> = ({
  options = [],
  id = '',
  leadFieldAssociated,
  maxSelection = 0,
  automaticNext = false,
  isMultiQuestionChild = false,
  title,
  stepName,
  dynamicOptionsFilters,
  variant,
  isOptionsLoading = false,
  dynamicOptionsError,
}) => {
  const { updateFlowState, getStepMetadataById, handleStepForward } =
    useDynamicExperienceContext();
  const stepData = getStepMetadataById(id) || {};
  const selectedOptions: Option[] = (stepData.option as Option[]) || [];
  const { dynamicOptions, isLoading, fetchError } = useOfferingsFilterOptions(
    dynamicOptionsFilters,
  );

  const { ref: visibilityRef, triggerFieldInputted } = useQuestionEvents({
    id,
    fieldType: 'multiple_choice',
    options,
    title,
    stepName,
    isMultiQuestionChild,
    variant: variant || 'dynamic_multiple_choice',
    leadFieldAssociated,
  });

  const allOptions = dynamicOptions;

  const displayedOptions = useMemo(() => {
    const first10 = allOptions.slice(0, 10);
    const first10Ids = new Set(first10.map((opt) => opt.optionId));

    const selectedNotInFirst10 = selectedOptions.filter(
      (opt) => !first10Ids.has(opt.optionId),
    );

    if (selectedNotInFirst10.length > 0) {
      return [...selectedNotInFirst10, ...first10];
    }

    return first10;
  }, [allOptions, selectedOptions]);

  const searchableOptions = useMemo(() => {
    const displayedIds = new Set(displayedOptions.map((opt) => opt.optionId));
    return allOptions.filter((opt) => !displayedIds.has(opt.optionId));
  }, [allOptions, displayedOptions]);

  const handleOptionClick = (clickedOption: Option) => {
    const isSelected = selectedOptions.find(
      (opt) => opt.optionId === clickedOption.optionId,
    );

    let newSelectedOptions: Option[];

    if (isSelected) {
      newSelectedOptions = selectedOptions.filter(
        (opt) => opt.optionId !== clickedOption.optionId,
      );
    } else {
      if (maxSelection > 0 && selectedOptions.length >= maxSelection) {
        return;
      }
      newSelectedOptions = [...selectedOptions, clickedOption];
    }

    triggerFieldInputted(
      newSelectedOptions.map((opt) => opt.text as string).join(','),
    );

    updateFlowState(id, { option: newSelectedOptions, leadFieldAssociated });

    if (
      automaticNext &&
      !isMultiQuestionChild &&
      newSelectedOptions.length === maxSelection
    ) {
      handleStepForward();
    }
  };

  const handleSearchSelect = (option: Option) => {
    const isAlreadySelected = selectedOptions.some(
      (opt) => opt.optionId === option.optionId,
    );

    if (isAlreadySelected) return;
    if (maxSelection > 0 && selectedOptions.length >= maxSelection) return;

    const newSelectedOptions = [...selectedOptions, option];

    updateFlowState(id, { option: newSelectedOptions, leadFieldAssociated });

    triggerFieldInputted(
      newSelectedOptions.map((opt) => opt.text as string).join(','),
    );

    if (
      automaticNext &&
      !isMultiQuestionChild &&
      newSelectedOptions.length === maxSelection
    ) {
      handleStepForward();
    }
  };

  const getSelectionNumber = (option: Option): number | null => {
    const index = selectedOptions.findIndex(
      (opt) => opt.optionId === option.optionId,
    );
    return index !== -1 ? index + 1 : null;
  };

  const effectiveError = dynamicOptionsError ?? fetchError;
  const showOptionsLoading = (isLoading || isOptionsLoading) && !effectiveError;
  const isEmpty =
    !isLoading &&
    !isOptionsLoading &&
    !effectiveError &&
    allOptions.length === 0;

  return (
    <>
      {effectiveError ? (
        <ErrorMessage
          message={effectiveError}
          className='w-full justify-center py-2 px-2'
        />
      ) : null}
      {isEmpty ? (
        <p
          role='status'
          className='w-full text-center text-sm text-gray-500 py-2 px-2'
        >
          No options found
        </p>
      ) : null}
      <div
        className='flex flex-wrap gap-4 py-6 font-normal w-full items-center justify-center'
        ref={visibilityRef as LegacyRef<HTMLDivElement>}
      >
        {showOptionsLoading
          ? Array.from({ length: 10 }).map((_, index) => (
              <div
                key={index}
                className='animate-pulse rounded-full bg-gray-300 h-10'
                style={{ width: `${120 + (index % 3) * 40}px` }}
              />
            ))
          : displayedOptions.map((option, index) => {
              const isSelected = selectedOptions.some(
                (opt) => opt.optionId === option.optionId,
              );
              const selectionNumber = getSelectionNumber(option);

              return (
                <button
                  type='button'
                  role='button'
                  key={index}
                  className={`mx-0 transition-all duration-200 ease-in-out mb-0 flex w-fit flex-initial cursor-pointer items-center justify-center gap-x-2 rounded-full border-2 px-5 py-2 text-sm hover:border-gray-300 hover:bg-gray-100 ${
                    isSelected
                      ? 'bg-secondary-800 text-white border-secondary-700'
                      : ''
                  }`}
                  onClick={() => handleOptionClick(option)}
                >
                  {isSelected && (
                    <span className='w-5 h-5 flex items-center justify-center'>
                      {selectionNumber}.
                    </span>
                  )}
                  {option.text}
                </button>
              );
            })}
      </div>

      {showOptionsLoading && (
        <div className='w-full relative mb-6'>
          <div className='animate-pulse rounded-lg bg-gray-300 h-11 w-full' />
        </div>
      )}

      {!showOptionsLoading && allOptions.length > 10 && (
        <SearchBar
          options={searchableOptions}
          selectedOptions={selectedOptions}
          maxSelection={maxSelection}
          onSelect={handleSearchSelect}
        />
      )}
    </>
  );
};

export default DynamicMultipleChoiceQuestion;
